import * as React from 'react';

import {
  SubscriptionGenqlSelection,
  Subscription,
  FieldsSelection,
  generateSubscriptionOp,
  inlineFragments,
} from '@rematter/frontend-lib-api-client';
import type { ExecutionResult } from 'graphql-sse';
import camelCase from 'lodash/camelCase';

import { useApi } from '~/shared/hooks';

export type SubscriptionHookOptions<TData, TResult = TData> = {
  alias?: string;
  skip?: boolean;
  select?: (data: TData) => TResult;
  onEvent?: (data: TResult) => void;
};

export type SubscriptionHookResult<TResult> = {
  data?: TResult;
  error?: Error;
};

export function createSubscriptionHook<
  TParams,
  TSelection extends SubscriptionGenqlSelection,
  TData = FieldsSelection<Subscription, TSelection>,
  TResult = TData,
>(
  key: `${string}.${string}`, // 'transportation.trucks'
  selectionFn: (params: TParams) => TSelection,
  options?: SubscriptionHookOptions<TData, TResult>,
) {
  return function useGenqlSubscription(
    params?: TParams,
    hookOptions?: Pick<
      SubscriptionHookOptions<TData, TResult>,
      'skip' | 'onEvent'
    >,
  ): SubscriptionHookResult<TResult> {
    const { sseClient } = useApi();

    const [data, setData] = React.useState<TResult>();
    const [error, setError] = React.useState<Error>();

    // Create stable ref to sseClient to prevent re-subscriptions
    const sseClientRef = React.useRef(sseClient);
    React.useEffect(() => {
      sseClientRef.current = sseClient;
    }, [sseClient]);

    // Only skip if explicitly set via hookOptions.skip
    // For subscriptions with void params (no parameters), params will be undefined but we should NOT skip
    const skip = hookOptions?.skip ?? false;

    const paramsJson = React.useMemo(
      () => (params === undefined ? 'null' : JSON.stringify(params)),
      [params],
    );

    React.useEffect(() => {
      if (skip) {
        return;
      }

      const parsedParams = (
        params === undefined ? undefined : JSON.parse(paramsJson)
      ) as TParams;
      const selection = selectionFn(parsedParams);
      const queryParams = generateSubscriptionOp({
        ...selection,
        __name: options?.alias ?? camelCase(key),
      });

      // Inlines all fragments. This is a limitation of gqlgen on the backend, that fails to serialize
      // unions in fragments in subscriptions, which is a pretty common pattern.
      queryParams.query = inlineFragments(queryParams.query);

      const dispose = sseClientRef
        .current(`subscription.${key}`)
        .subscribe<ExecutionResult<TData>>(queryParams, {
          next(value) {
            if (value.data) {
              const data = value.data as TData;
              // Transform data with select function if provided
              const transformedData = options?.select
                ? options.select(data)
                : (data as unknown as TResult);

              // Call onEvent callback immediately if provided (bypasses React state)
              // hookOptions.onEvent takes precedence over options.onEvent
              const eventCallback = hookOptions?.onEvent ?? options?.onEvent;
              if (eventCallback) {
                eventCallback(transformedData);
              } else {
                setData(transformedData);
              }
            }
          },
          error(err: Error | ExecutionResult<TData>) {
            const errorObj =
              err instanceof Error ? err : new Error(JSON.stringify(err));
            setError(errorObj);
          },
          complete() {
            // Intentionally kept empty
          },
        });

      return () => {
        dispose();
      };
    }, [paramsJson, skip, key]);

    return { data, error };
  };
}
